import { DOCUMENT } from '@angular/common';
import { Injectable, Signal, computed, inject, signal } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { LanguageApiService } from './language.service';
import { Language } from '../interfaces/language.interface';


export const DEFAULT_LANGUAGE_CODE = 'es';
const LOCALE_STORAGE_KEY = 'cms_locale';

@Injectable({ providedIn: 'root' })
export class LanguageStateService {
  readonly #api = inject(LanguageApiService);
  readonly #translate = inject(TranslateService);
  readonly #document = inject(DOCUMENT);

  readonly #languages = signal<Language[]>([]);
  readonly #activeCode = signal<string | null>(this.#resolveInitialCode());

  readonly languages: Signal<Language[]> = this.#languages.asReadonly();

  readonly activeLanguage: Signal<Language | undefined> = computed(() =>
    this.#languages().find((lang) => lang.code === this.activeCode()),
  );

  readonly activeCode: Signal<string> = computed(() => this.#activeCode() ?? DEFAULT_LANGUAGE_CODE);

  constructor() {
    this.#syncTranslate(this.activeCode());
  }

  async load(): Promise<void> {
    const languages = await firstValueFrom(this.#api.getAll());
    this.#languages.set(languages);

    const storedCode = this.#storage()?.getItem(LOCALE_STORAGE_KEY);
    const isStoredValid = languages.some((lang) => lang.code === storedCode);

    if (isStoredValid && storedCode) {
      this.setActiveCode(storedCode);
      return;
    }

    const defaultLang = languages.find((lang) => lang.is_default);
    this.setActiveCode(defaultLang?.code ?? DEFAULT_LANGUAGE_CODE);
  }

  setActiveCode(code: string): void {
    this.#activeCode.set(code);
    this.#syncTranslate(code);
    this.#storage()?.setItem(LOCALE_STORAGE_KEY, code);
  }

  #resolveInitialCode(): string | null {
    return this.#storage()?.getItem(LOCALE_STORAGE_KEY) ?? null;
  }

  #syncTranslate(code: string): void {
    this.#translate.use(code);
  }

  #storage(): Storage | null {
    return this.#document.defaultView?.localStorage ?? null;
  }
}
